import { refreshAccessToken } from './authStore'

/**
 * Wrap an RTK Query base query (e.g. the result of `fetchBaseQuery`) so that a 401 triggers a
 * single shared refresh and one retry of the original request. Pair with a `prepareHeaders`
 * that reads the token from {@link getAccessToken}, so the retry picks up the rotated token.
 *
 * The generic is constrained only to "a base-query-shaped function" and returns the exact same
 * type, so RTK's endpoint/result-type inference is fully preserved and this shared package
 * takes no dependency (runtime or type) on @reduxjs/toolkit.
 *
 * Usage:
 *   const raw = fetchBaseQuery({ baseUrl: '/api', prepareHeaders })
 *   baseQuery: withReauth(raw)
 */
export function withReauth<BQ extends (args: any, api: any, extraOptions: any) => any>(
  rawBaseQuery: BQ,
): BQ {
  const wrapped = async (args: any, api: any, extraOptions: any) => {
    let result = await rawBaseQuery(args, api, extraOptions)
    if (result?.error && result.error.status === 401) {
      const token = await refreshAccessToken()
      if (token) {
        result = await rawBaseQuery(args, api, extraOptions)
      }
    }
    return result
  }
  return wrapped as BQ
}
